<template>
  <b-card title="Построитель игр">
    <b-button
      variant="primary"
      class="mb-1"
      :to="{ name: 'game-builder-create' }"
    >
      <feather-icon
        icon="PlusCircleIcon"
        class="mr-50"
      />
      Создать
    </b-button>

    <vue-good-table
      :columns="columns"
      :rows="gamesList"
      :pagination-options="{
        enabled: true,
        perPage: filters.perPage,
      }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <span v-if="props.column.field === 'progress'">
          <b-progress
            :value="getProgress(props.row)"
            :max="getTotalTopics(props.row)"
            :variant="getProgress(props.row) === getTotalTopics(props.row) ? 'success' : 'primary'"
            class="progress-bar-sm"
            style="min-width: 80px"
          />
          <small>{{ getProgress(props.row) }}/{{ getTotalTopics(props.row) }}</small>
        </span>

        <span v-else-if="props.column.field === 'status'">
          <b-badge :variant="isGameComplete(props.row) ? 'light-success' : 'light-warning'">
            {{ isGameComplete(props.row) ? 'Завершена' : 'В процессе' }}
          </b-badge>
        </span>

        <span v-else-if="props.column.field === 'created_at'">
          {{ formatDate(props.row.created_at) }}
        </span>

        <span v-else-if="props.column.field === 'action'">
          <b-link
            :to="{ name: 'game-builder', params: { id: props.row.id } }"
            class="font-weight-bold mr-1"
          >
            <feather-icon icon="Edit2Icon" />
          </b-link>
          <feather-icon
            icon="TrashIcon"
            class="cursor-pointer text-danger"
            @click="confirmDelete(props.row)"
          />
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div>
            <b-pagination
              v-model="filters.page"
              :total-rows="total"
              :per-page="filters.perPage"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
  BCard, BButton, BPagination, BBadge, BLink, BProgress,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { onUnmounted, ref, watch } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatDate } from '@core/utils/filter'
import gameBuilderStoreModule from './gameBuilderStoreModule'

export default {
  components: {
    BCard,
    BButton,
    BPagination,
    BBadge,
    BLink,
    BProgress,
    VueGoodTable,
  },
  setup() {
    const STORE_MODULE_NAME = 'game-builder'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, gameBuilderStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const columns = [
      { label: 'ID', field: 'id', type: 'number' },
      { label: 'Название', field: 'name' },
      { label: 'Прогресс', field: 'progress', sortable: false },
      { label: 'Статус', field: 'status', sortable: false },
      { label: 'Дата', field: 'created_at' },
      { label: 'Действия', field: 'action', sortable: false },
    ]

    const filters = ref({
      page: 1,
      perPage: 15,
    })

    const gamesList = ref([])
    const total = ref(0)

    const getProgress = row => {
      if (row.progress) return row.progress.filled_themes
      return 0
    }

    const getTotalTopics = row => {
      if (row.progress) return row.progress.total_themes
      return 0
    }

    const isGameComplete = row => {
      if (row.status === 'finalized' || row.status === 'completed') return true
      const t = getTotalTopics(row)
      return t > 0 && getProgress(row) === t
    }

    const fetchList = () => {
      store.dispatch('game-builder/fetchList', {
        page: filters.value.page,
        perPage: filters.value.perPage,
      })
        .then(response => {
          gamesList.value = response.data.data
          total.value = response.data.meta?.total || response.data.data.length
        })
    }

    fetchList()

    watch(filters, () => {
      fetchList()
    }, { deep: true })

    const confirmDelete = row => {
      if (!window.confirm(`Удалить игру #${row.id}?`)) return
      store.dispatch('game-builder/deleteGame', { id: row.id })
        .then(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Игра удалена',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          fetchList()
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Ошибка при удалении',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    return {
      columns,
      gamesList,
      filters,
      total,
      getProgress,
      getTotalTopics,
      isGameComplete,
      confirmDelete,
      formatDate,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
